import { GridViewMetadata } from '../../../grid/gridView/types';
import { ColumnDetails, ColumnType, ColumnValue } from '../../../grid/types/common';
import { PhasrRecommendationsGridDataProvider } from '../dataProvider/PhasrRecommendationsGridDataProvider';
import { Filter } from '../../../grid/gridFilters/types';
import { translateFn } from '../../../utils/translator.helper';
import {
  actionButtonsRenderer,
  companyRenderer,
  createdOnRenderer,
  renderAttackSurfaceExposure,
  renderBehavioralProfiles,
  renderRuleIdDependentColumns,
  renderTargetActivityType,
} from './customGridRenderers';
import { getViewState } from '../../../utils/states.utils';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { RemoteDetailsTypes } from '../../../filters/backendEnumFilter/types';
import { LabelWidthValues } from '../../../types/common/types';
import { Column, ColumnKeys, DomSchema } from '../types/grid';
import { Dependencies, PhasrRecommendationsWebComponentSchema, RESTRICT_ACTION } from '../types';
import { API_TYPE_PHASR } from '../../../webcomponents.constants';
import { GetItemsResponse } from '../../../ui/gzSingleSelectComplex/types';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';
import { PhasrSidePanelComponent } from '../../../phasrSidePanel';
import { PhasrPanelComponentTypes } from '../../../phasrSidePanel/types/types';
import { PhasrRecommendationSideMenuWebComponent } from '../../../phasrSideMenus/phasrRecommendationSideMenu';
import {
  getPhasrActionTakenTypesName,
  getPhasrCategoryDictionaryToPrefferedName,
  getPhasrCategoryTypesName,
  PhasrDictionaryEntry,
} from '../../../types/common/phasrTypes';
import { GridEntry } from '../types/phasr-recommendations-grid-entry';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as ColumnKeysMonitoredRules } from '../../../phasrMonitoredRules/phasrMonitoredRulesGrid/types';

export class GridConfig {
  public dictionary = [];

  private dom: DomSchema; // used in customRenderers through bind(this)
  private readonly columnRenderers = {
    [ColumnKeys.RECOMMENDATION_NAME]: renderRuleIdDependentColumns.bind(this),
    [ColumnKeys.DESCRIPTION]: renderRuleIdDependentColumns.bind(this),
    [ColumnKeys.MONITORED_RULE]: renderRuleIdDependentColumns.bind(this),
    [ColumnKeys.PHASR_CATEGORIES]: renderTargetActivityType.bind(this),
    [ColumnKeys.ATTACK_SURFACE_EXPOSURE]: renderAttackSurfaceExposure.bind(this),
    [ColumnKeys.CREATED_ON]: createdOnRenderer.bind(this),
    [ColumnKeys.COMPANY]: companyRenderer.bind(this),
    [ColumnKeys.SELECT]: actionButtonsRenderer.bind(this),
    [ColumnKeys.BEHAVIORAL_PROFILES]: renderBehavioralProfiles.bind(this),
  };

  private readonly pageSizeOptions = [
    {
      title: '20',
      value: '20',
    },
    {
      title: '50',
      value: '50',
    },
    {
      title: '100',
      value: '100',
    },
  ];

  private readonly filtersUiConfigs = [
    {
      key: ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES,
      config: {
        labelWidth: LabelWidthValues.LARGE
      }
    },
    {
      key: ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES,
      config: {
        labelWidth: LabelWidthValues.LARGE
      }
    },
    {
      key: ColumnKeys.CREATED_ON,
      config: {
        enableSelectAllToDate: true,
        additionalTimeRangeOptions: [
          {
            label: translateFn('Last 90 days'),
            value: 90,
          },
        ],
      }
    },
    {
      key: ColumnKeys.COMPANY,
      config: {
        apiType: API_TYPE_PHASR,
        populateFilterCallback: (filterComponent, value) => {
          const selectedValues: GetItemsResponse = {
            items: [],
            totalItemsCount: 0,
          };

          filterComponent.setFilterOptions(selectedValues).then(() => {
            const itemFound = filterComponent.filterOptions.find(option => option.value === value.exactMatch);
            filterComponent.setFormValue(itemFound ? itemFound.value : null);
          });
        },
      },
    }
  ];

  private readonly defaultCompactViewChecked = false;
  private readonly defaultPageSize = 100;
  private dependencies: Dependencies;
  private defaultCompanyId = '';

  public async getDataForView(dependencies: Dependencies, dom: DomSchema): Promise<PhasrRecommendationsWebComponentSchema> {
    this.dependencies = dependencies;
    try {
      this.defaultCompanyId = await dependencies.api.clients.Company.getRootOrFirstAvailableCompany({},
        getRequestDefaultOptions()
      );
    } catch (error) {
      this.defaultCompanyId = '';
    }
    this.dom = dom;
    const dataProvider = new PhasrRecommendationsGridDataProvider(dependencies, this.getColumnDetails());
    this.dictionary = Application.Main.getPhasrDictionary();
    const gridState = getViewState(dataProvider.viewStateName);

    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'gz-phasr-recommendations-grid-default-view',
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(),
      columns: gridState.columns ?? this.getColumns(),
      filters: gridState.filters ?? this.getDefaultFilters(),
      pageSize: gridState.pageSize ?? 20,
      isCompactViewChecked: gridState.compactView ?? false,
      hasPagination: true,
      pageSizeOptions: this.pageSizeOptions,
      columnRenderers: this.columnRenderers,
      showGlobalActions: false,
      allColumnDetails: this.getColumnDetails(),
      pageNumber: 1,
      dataProvider: dataProvider,
      enableCSVExport: false,
      cellClassNameGenerator: this.getCellClassNameGenerator,
      columnConfigs: [],
      filtersUiConfigs: this.filtersUiConfigs,
      sortColumnName: ColumnKeys.ATTACK_SURFACE_EXPOSURE,
      sortDirection: 'desc',
    };

    return {
      viewMetaData,
      defaultViewProps: {
        displayedColumns: this.getColumns(),
        filters: this.getDefaultFilters(),
        pageSize: this.defaultPageSize,
        compactView: this.defaultCompactViewChecked,
      },
    };
  }

  public getDefaultFilters(): Array<Filter<ColumnKeys>> {
    let defaultFilters: Array<Filter<ColumnKeys>> = [
      {
        [ColumnKeys.RECOMMENDATION_NAME]: {in: ['all']},
      },
      {
        [ColumnKeys.CREATED_ON]: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        [ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES]: {in: ['all']},
      },
      {
        [ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES]: {in: ['all']},
      },
    ];

    if (this.dependencies.permissions.canHaveChildCompanies) {
      defaultFilters = [
        {
          [ColumnKeys.COMPANY]: {
            exactMatch: this.defaultCompanyId,
          },
        },
        ...defaultFilters,
      ];
    }

    return defaultFilters;
  }

  private getPredefinedFiltersKeys(): Array<ColumnKeys> {
    let keys: Array<ColumnKeys> = [ColumnKeys.RECOMMENDATION_NAME, ColumnKeys.CREATED_ON, ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES, ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES];
    if (this.dependencies.permissions.canHaveChildCompanies) {
      keys = [ColumnKeys.COMPANY, ...keys];
    }

    return keys;
  }

  private getColumns(): Column[] {
    return [Column.RECOMMENDATION_NAME, Column.ATTACK_SURFACE_EXPOSURE, Column.CREATED_ON, Column.DESCRIPTION,
      Column.PHASR_CATEGORIES, Column.MONITORED_RULE, Column.BEHAVIORAL_PROFILES, Column.ACTION_TAKEN, Column.COMPANY,
      Column.SELECT];
  }

  private getColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
    const columnDetails: Array<ColumnDetails<Column, ColumnKeys>> = [
      {
        column: Column.RECOMMENDATION_NAME,
        key: ColumnKeys.RECOMMENDATION_NAME,
        columnName: translateFn('Name'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: false,
        sortable: false,
        filteringDisabled: false,
        flexGrow: 2,
        remoteDetails: {
          type: RemoteDetailsTypes.PhasrRecommendationName,
        },
      },
      {
        column: Column.ATTACK_SURFACE_EXPOSURE,
        key: ColumnKeys.ATTACK_SURFACE_EXPOSURE,
        columnName: translateFn('Attack surface reduction'),
        type: ColumnType.NUMBER,
        removable: false,
        sortable: true,
        filteringDisabled: true,
        flexGrow: 1,
      },
      {
        column: Column.CREATED_ON,
        key: ColumnKeys.CREATED_ON,
        columnName: translateFn('Created on'),
        type: ColumnType.DATE,
        removable: false,
        sortable: true,
        filteringDisabled: true,
        flexGrow: 1,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        column: Column.DESCRIPTION,
        key: ColumnKeys.DESCRIPTION,
        columnName: translateFn('Description'),
        type: ColumnType.STRING,
        removable: false,
        sortable: false,
        filteringDisabled: true,
        flexGrow: 1,
      },
      {
        column: Column.PHASR_CATEGORIES,
        key: ColumnKeys.PHASR_CATEGORIES,
        columnName: translateFn('Targeted activity type'),
        type: ColumnType.ENUM,
        values: this.getCategoryTypesNames(),
        removable: false,
        sortable: false,
        filteringDisabled: false,
        flexGrow: 1,
      },
      {
        column: Column.MONITORED_RULE,
        key: ColumnKeys.MONITORED_RULE,
        columnName: translateFn('Monitored rule'),
        type: ColumnType.CUSTOM,
        removable: true,
        sortable: false,
        filteringDisabled: true,
        flexGrow: 1,
      },
      {
        column: Column.BEHAVIORAL_PROFILES,
        key: ColumnKeys.BEHAVIORAL_PROFILES,
        columnName: translateFn('Behavioral profiles'),
        type: ColumnType.CUSTOM,
        removable: true,
        sortable: false,
        filteringDisabled: true,
        flexGrow: 1,
      },
      {
        column: Column.ACTION_TAKEN,
        key: ColumnKeys.ACTION_TAKEN,
        columnName: translateFn('Action taken'),
        type: ColumnType.ENUM,
        values: this.getActionTakenTypesNames(),
        removable: false,
        sortable: false,
        filteringDisabled: false,
        flexGrow: 1,
      },
      {
        column: Column.BEHAVIORAL_PROFILE_IDENTITIES,
        key: ColumnKeys.BEHAVIORAL_PROFILE_IDENTITIES,
        columnName: translateFn('Behavioral profile identities'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        flexGrow: 1,
        isHiddenInGridSettings: true,
        filteringDisabled: false,
        remoteDetails: {
          type: RemoteDetailsTypes.PhasrBehavioralProfileIdentities,
        },
      },
      {
        column: Column.BEHAVIORAL_PROFILE_RESOURCES,
        key: ColumnKeys.BEHAVIORAL_PROFILE_RESOURCES,
        columnName: translateFn('Behavioral profile resources'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        flexGrow: 1,
        isHiddenInGridSettings: true,
        filteringDisabled: false,
        remoteDetails: {
          type: RemoteDetailsTypes.PhasrBehavioralProfileResources,
        },
      },
    ];

    if (this.dependencies.permissions.canHaveChildCompanies) {
      columnDetails.push({
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: true,
        sortable: false,
        filterResetValue: {
          exactMatch: this.defaultCompanyId,
        }
      });
    }

    columnDetails.push({
      column: Column.SELECT,
      key: ColumnKeys.SELECT,
      columnName: '',
      type: ColumnType.ACTION,
      removable: false,
      sortable: false,
    });

    return columnDetails;
  }

  private getCellClassNameGenerator(_, rowData: any): string {
    const {
      item: {isGhost},
    } = rowData;

    return isGhost ? 'cell-ghost' : '';
  }

  private getCategoryTypesNames(): ColumnValue[] {
    return getPhasrCategoryTypesName();
  }

  private getActionTakenTypesNames(): ColumnValue[] {
    return getPhasrActionTakenTypesName();
  }

  /**
   * Used for renderer event handling
   * renderBehavioralProfiles()
   *
   * @param dom
   * @param labelClass
   * @param svgClass
   * @param item
   * @private
   */
  private handleBehavioralProfilesClick(dom: DomSchema, labelClass: string, svgClass: string, item: GridEntry) {
    labelClass = '.' + labelClass;
    svgClass = '.' + svgClass;

    dom.$grid.shadowRoot?.querySelector(`${labelClass}`)?.setAttribute('style', 'color: var(--blue-layout-300-active); padding-left: 3px;');
    dom.$grid.shadowRoot?.querySelector(`${svgClass}`)?.setAttribute('fill', 'var(--blue-layout-300-active)');

    const behavioralProfilesContent = this.getBehavioralProfilesPanel(dom, item);
    dom.$behavioralProfilesPanel.setMenuContent(behavioralProfilesContent);
    dom.$behavioralProfilesPanel.openSideMenu();
    dom.$behavioralProfilesPanel.addEventListener(
      SideMenuEvents.CLOSE,
      () => {
        dom.$grid.shadowRoot?.querySelector(`${labelClass}`)?.setAttribute('style', 'color: var(--body-text-arrows-400); padding-left: 3px;');
        dom.$grid.shadowRoot?.querySelector(`${svgClass}`)?.setAttribute('fill', 'var(--chart-302-icon-buttons-titles)');
      }
    );
  }

  /**
   * Used by renderers that get bind to this from this class on event trigger
   * renderRuleIdDependentColumns()
   *
   */
  private handleRecommendationNameClick(dom: DomSchema, item: GridEntry, dictionary: Partial<PhasrDictionaryEntry>) {
    const recommendationDetailsPanel = new PhasrRecommendationSideMenuWebComponent();
    recommendationDetailsPanel.setRecommendationData({
      id: item.objectId ?? '',
      name: dictionary?.[item.type === RESTRICT_ACTION ? 'rule_recommendation' : 'rule_allow_name'] ?? 'N/A',
      description: dictionary?.rule_description ?? 'N/A',
      createdOn: item.createdOn,
      behavioralProfiles: item.behavioralProfiles,
      attackSurfaceReduction: item.attackSurfaceReduction,
      actionTaken: item.actionTaken,
      mitigation: dictionary?.[item.type === RESTRICT_ACTION ? 'rule_mitigation' : 'rule_allow_mitigation'] ?? 'N/A',
      rule: dictionary?.rule_name ?? 'N/A',
      ruleId: item.ruleId ?? 'N/A',
      targetedActivityType: getPhasrCategoryDictionaryToPrefferedName()[dictionary.category ?? 'N/A'] ?? 'N/A',
      companyId: item.companyId ?? '',
      type: item.type ?? 0,
      phasrUsable: item.phasrUsable ?? false,
    });
    recommendationDetailsPanel.injectDependencies(this.dependencies);
    recommendationDetailsPanel.start().catch(console.error);

    dom.$detailsPanel.setMenuContent(recommendationDetailsPanel);
    dom.$detailsPanel.openSideMenu();
  }

  /**
   * Used by renderers that get bind to this from this class on event trigger
   * renderRuleIdDependentColumns()
   *
   */
  private handleRuleNameClick(data: GridEntry) {
    const params = {
      filters: [
        {
          [ColumnKeysMonitoredRules.COMPANY]: {
            exactMatch: data.companyId,
          },
        },
        {
          [ColumnKeysMonitoredRules.RULE_TRIGGER_DATE]: {
            timeRange: {
              kind: TimeRangeKind.ALL_TO_DATE,
            },
          }
        },
        {
          [ColumnKeysMonitoredRules.RULE_NAME]: {
            complexEnumFilter: [
              {
                value: data.ruleId,
                label: this.dictionary[data.ruleId]?.['rule_name'] ?? 'N/A',
              }
            ]
          }
        }
      ],
      openSideMenuForFirstItem: true,
    };

    NavigationHelper.navigateTo('/phasr-monitored-rules', params);
  }

  private getBehavioralProfilesPanel(dom: DomSchema, item: GridEntry): PhasrSidePanelComponent {
    const sidePanelContent = new PhasrSidePanelComponent();
    sidePanelContent.componentSpecificData = {
      recommendationId: item.objectId ?? '',
    }
    sidePanelContent.companyId = dom.$grid.filters[0].company?.exactMatch ?? ''; // first filter is always company filter
    sidePanelContent.titleText = translateFn('Behavioral profiles');
    sidePanelContent.componentType = PhasrPanelComponentTypes.BehavioralProfilesNoToggle
    sidePanelContent.injectDependencies(this.dependencies);
    sidePanelContent.start().catch(console.error);
    return sidePanelContent;
  }
}
